/** Minimal public-site events. Never pass DOM text, URLs, identities or trace data here. */
const surfaces = {
  '/': 'home',
  '/demo/insurance': 'insurance_example',
  '/demo/support': 'support_example',
  '/demo/document-review': 'document_example',
} as const;
const blogSurfaces = ['blog_listing', 'blog_article'] as const;
export type BlogSurface = (typeof blogSurfaces)[number];
type Surface = (typeof surfaces)[keyof typeof surfaces] | BlogSurface;
export type ProductAnalyticsAction = 'view' | 'quickstart' | 'installation';
const actions: ProductAnalyticsAction[] = ['view', 'quickstart', 'installation'];
const allowedKeys = new Set(
  [...Object.values(surfaces), ...blogSurfaces].flatMap((surface) =>
    actions.map((action) => `${surface}:${action}`)
  )
);
const storageKey = 'sentrial.first-use.v1';
const lifetime = 30 * 60 * 1000;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isCanonicalHost = (host: string) => ['sentrial.com', 'www.sentrial.com'].includes(host);

export function publicSurface(pathname: string): Surface | null {
  return Object.hasOwn(surfaces, pathname) ? surfaces[pathname as keyof typeof surfaces] : null;
}

/** Referrer path/query and arbitrary campaign values never enter an event. */
export function acquisitionSource(referrer: string): string {
  if (!referrer) return 'direct_or_unavailable';
  try {
    const { hostname, protocol } = new URL(referrer);
    if (protocol !== 'https:' && protocol !== 'http:') return 'other';
    if (isCanonicalHost(hostname)) return 'internal_navigation';
    const sources = {
      'google.com': 'google',
      'bing.com': 'bing',
      'duckduckgo.com': 'duckduckgo',
      'youtube.com': 'youtube',
      'linkedin.com': 'linkedin',
      'x.com': 'x',
      't.co': 'x',
      'github.com': 'github',
      'chatgpt.com': 'chatgpt',
      'perplexity.ai': 'perplexity',
    };
    for (const [domain, source] of Object.entries(sources))
      if (hostname === domain || hostname.endsWith(`.${domain}`)) return source;
  } catch {
    /* Malformed or opaque referrer: no raw value escapes. */
  }
  return 'other';
}

type Session = { id: string; expiresAt: number; seen: string[] };
export type ProductAnalytics = ReturnType<typeof createProductAnalytics>;
export function createProductAnalytics(options: {
  apiKey: string | undefined;
  hostname: string;
  optedOut: () => boolean;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  send: typeof fetch;
  trafficCohort?: () => 'internal' | 'unclassified';
  now?: () => number;
  uuid?: () => string;
}) {
  const now = options.now ?? Date.now;
  const uuid = options.uuid ?? (() => crypto.randomUUID());
  let session: Session | null = null;
  let limited = false;
  function save() {
    try {
      options.storage.setItem(storageKey, JSON.stringify(session));
    } catch {
      /* In-memory only. */
    }
  }
  function getSession() {
    if (session && session.expiresAt > now()) return session;
    try {
      const raw = options.storage.getItem(storageKey);
      const stored = raw && raw.length < 4096 ? JSON.parse(raw) : null;
      if (
        stored &&
        uuidPattern.test(stored.id) &&
        Number.isFinite(stored.expiresAt) &&
        stored.expiresAt > now() &&
        stored.expiresAt <= now() + lifetime &&
        Array.isArray(stored.seen) &&
        stored.seen.length <= allowedKeys.size &&
        stored.seen.every((key: unknown) => typeof key === 'string' && allowedKeys.has(key))
      ) {
        session = {
          id: stored.id,
          expiresAt: stored.expiresAt,
          seen: [...new Set<string>(stored.seen)],
        };
        return session;
      }
    } catch {
      /* Unavailable storage never blocks navigation. */
    }
    session = { id: uuid(), expiresAt: now() + lifetime, seen: [] };
    save();
    return session;
  }
  async function recordSurface(
    surface: Surface | null,
    action: ProductAnalyticsAction,
    referrer: string
  ) {
    if (
      !surface ||
      !allowedKeys.has(`${surface}:${action}`) ||
      !options.apiKey?.trim() ||
      !isCanonicalHost(options.hostname) ||
      options.optedOut() ||
      limited
    )
      return false;
    try {
      const current = getSession(),
        key = `${surface}:${action}`;
      if (current.seen.includes(key)) return false;
      // One attempt per surface/action/session, including ambiguous network failures.
      // No automatic retries: the vendor does not promise idempotent event ingestion.
      current.seen.push(key);
      save();
      const response = await options.send(
        'https://ingest.humanbehavior.co/api/ingestion/customEvent',
        {
          method: 'POST',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          redirect: 'error',
          keepalive: true,
          signal: AbortSignal.timeout(4000),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${options.apiKey.trim()}`,
          },
          body: JSON.stringify({
            sessionId: current.id,
            // Ephemeral anonymous identity; no Supabase user, email or workspace identity.
            endUserId: current.id,
            eventName:
              action !== 'view'
                ? 'sentrial_setup_guide_opened'
                : blogSurfaces.includes(surface as BlogSurface)
                  ? 'sentrial_public_content_viewed'
                  : 'sentrial_first_use_viewed',
            eventProperties: {
              event_id: uuid(),
              schema_version: 1,
              surface,
              action,
              content_kind:
                surface === 'home'
                  ? 'marketing'
                  : blogSurfaces.includes(surface as BlogSurface)
                    ? 'editorial'
                    : 'recorded_sample',
              traffic_cohort:
                options.trafficCohort?.() === 'internal' ? 'internal' : 'unclassified',
              acquisition_source: acquisitionSource(referrer),
            },
          }),
        }
      );
      if (response.status === 429) limited = true;
      if (!response.ok) return false;
      const result = await response.json();
      if (result.monthlyLimitReached === true) limited = true;
      return result.success === true && !limited;
    } catch {
      return false;
    }
  }
  return {
    record: (pathname: string, action: ProductAnalyticsAction, referrer: string) =>
      recordSurface(publicSurface(pathname), action, referrer),
    // Called only by a successfully rendered blog page, never by guessing a URL prefix.
    // The article/category path is not part of the event or the stored deduplication key.
    recordBlog: (surface: BlogSurface, action: ProductAnalyticsAction, referrer: string) =>
      recordSurface(blogSurfaces.includes(surface) ? surface : null, action, referrer),
  };
}

let browserAnalytics: ProductAnalytics | null = null;
let internalVisit = false;
export function getProductAnalytics(): ProductAnalytics | null {
  if (typeof window === 'undefined') return null;
  if (!browserAnalytics) {
    // Access can throw in restricted browsers; the wrapper keeps an in-memory session.
    const storage = {
      getItem: (key: string) => window.sessionStorage.getItem(key),
      setItem: (key: string, value: string) => window.sessionStorage.setItem(key, value),
    };
    browserAnalytics = createProductAnalytics({
      apiKey: process.env.NEXT_PUBLIC_HUMANBEHAVIOR_API_KEY,
      hostname: window.location.hostname,
      storage,
      send: window.fetch.bind(window),
      optedOut: () =>
        navigator.doNotTrack === '1' ||
        (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true,
      trafficCohort: () => {
        // Explicit QA flag persists within this tab; arbitrary query values are never sent.
        if (new URLSearchParams(window.location.search).get('sentrial_test') === '1') {
          internalVisit = true;
          try {
            window.sessionStorage.setItem('sentrial.first-use.internal', '1');
          } catch {
            /* optional */
          }
        }
        try {
          internalVisit ||= window.sessionStorage.getItem('sentrial.first-use.internal') === '1';
        } catch {
          /* optional */
        }
        return internalVisit ? 'internal' : 'unclassified';
      },
    });
  }
  return browserAnalytics;
}
